import { useCallback, useEffect, useState } from "react";
//old import { UserPlus, Loader2 } from "lucide-react";
import { UserPlus } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../api/api";
import { Alert, Field, Panel, Pill, apiError, useOrgStyles } from "../../components/org/orgUi";
import { useOrgPortal } from "./orgPortalContext";
import PendingInvites from "./PendingInvites";
import { EmailText } from "./OrgStudents";
import InlineTableSkeleton from "../../components/layout/InlineTableSkeleton";

/** Coaches and org admins. A coach with no account yet gets an emailed invite
 *  to sign up as a coach, and joins here once they verify their email. */
export default function OrgStaff() {
    //old const { orgId, reloadOrg } = useOrgPortal();
    const { orgId, org, reloadOrg, isPlatformAdmin, goTo } = useOrgPortal();
    const s = useOrgStyles();
    // The org admin limit (a Community Partner has 3, Alicia Oct 16) shows where admins are added; once it's
    // reached Org admin is greyed out and says why, like the program limit on Programs
    const adminsFull = org.AdminLimit != null && org.AdminCount >= org.AdminLimit;
    const [staff, setStaff] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ email: "", isCoach: true, isOrgAdmin: false, assignLicense: false });
    const [invites, setInvites] = useState([]);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/staff`);
            setStaff(res.data.staff || []);
        } catch (err) {
            setError(apiError(err, "Couldn't load staff."));
        }
    }, [orgId]);

    const loadInvites = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/invites`);
            setInvites((res.data.invites || []).filter((i) => i.InviteType === "coach"));
        } catch { setInvites([]); }
    }, [orgId]);

    useEffect(() => { load(); loadInvites(); }, [load, loadInvites]);

    const act = async (fn, success) => {
        setBusy(true);
        setError("");
        setNotice("");
        try {
            const res = await fn();
            if (res?.data?.staff) setStaff(res.data.staff); else await load();
            await reloadOrg();
            const message = typeof success === "function" ? success(res?.data || {}) : success;
            if (message) setNotice(message);
            return true;
        } catch (err) {
            setError(apiError(err));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const add = async (e) => {
        e.preventDefault();
        const email = form.email.trim();
        const ok = await act(() => api.post(`/org/${orgId}/staff`, { ...form, assignLicense: form.isCoach && form.assignLicense }), (data) => {
            if (data.invited) {
                //old return `${email} doesn't have a coach account yet, so we emailed them an invitation. They'll show up here once they sign up and verify their email (Pono Pilot approves new coach accounts).`;
                // with Org admin ticked too, they still join as a coach first (Evan, Sep 30: org admins add other admins)
                return `${email} doesn't have a coach account yet, so we emailed them an invitation. They'll show up here once they sign up and verify their email (Pono Pilot approves new coach accounts).`
                    + (data.adminPending ? " They'll join as a coach; tick Org admin once they appear here." : "");
            }
            if (!form.isCoach) return "Added.";
            if (data.licenseError) return `Coach added, but no license was given: ${data.licenseError}`;
            //old return form.assignLicense ? "Coach added with a coach license." : "Coach added. Assign them a coach license so they can see your students.";
            return form.assignLicense ? "Coach added with a coach license." : "Coach added. Assign them a coach license so they can see your clients.";
        });
        if (ok) {
            setForm({ email: "", isCoach: true, isOrgAdmin: false, assignLicense: false });
            loadInvites();
        }
    };

    const updateRoles = (m, roles) => act(() => api.put(`/org/${orgId}/staff/${m.OrgStaffID}`, {
        isCoach: !!m.IsCoach, isOrgAdmin: !!m.IsOrgAdmin, ...roles,
    }));

    const remove = (m) => {
        if (!window.confirm(`Remove ${m.FirstName} ${m.LastName} from your organization? Any coach license they hold is removed.`)) return;
        act(() => api.delete(`/org/${orgId}/staff/${m.OrgStaffID}`));
    };

    return (
        <div className="grid gap-6 lg:grid-cols-3">
            {/* old <div className="space-y-6 lg:col-span-2"> */}
            {/* min-w-0: on a phone the staff table scrolls inside its panel instead of widening the page */}
            <div className="min-w-0 space-y-6 lg:col-span-2">
            <Panel title="Coaches & admins" padded={false}>
                <div className="px-5 pt-4">
                    <Alert onClose={() => setError("")}>{error}</Alert>
                    <Alert kind="success" onClose={() => setNotice("")}>{notice}</Alert>
                    {adminsFull && (
                        <p className={`mb-4 text-sm ${s.muted}`}>
                            {isPlatformAdmin
                                ? <>This organization has {org.AdminCount} of {org.AdminLimit} org admins, the most it can have. Raise the limit in{" "}
                                    <Link className={s.btnLink} to="/admin/organizations">Admin → Organizations</Link>.</>
                                : <>Your organization has {org.AdminCount} of {org.AdminLimit} org admins, the most it can have. Remove one first, or{" "}
                                    <button type="button" className={s.btnLink} onClick={() => goTo("requests", { feature: "other" })}>send us a request</button> to raise the limit.</>}
                        </p>
                    )}
                </div>
                {!staff ? (
                    //old <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div>
                    <InlineTableSkeleton isDarkMode={s.dark} rows={3} cols={5} bordered={false} />
                ) : (
                    //old <ul className={`divide-y ${s.divider}`}>
                        //old {staff.map((m) => (
                            //old <li key={m.OrgStaffID} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                                //old <div className="min-w-0">
                                    //old <div className={`font-semibold ${s.text}`}>{m.FirstName} {m.LastName}</div>
                                    //old <div className={`truncate text-xs ${s.muted}`}>{m.Email}</div>
                                    //old <div className="mt-1 flex flex-wrap gap-1">
                                        //old {m.IsOrgAdmin ? <Pill tone="primary">Org admin</Pill> : null}
                                        //old {m.IsCoach ? <Pill>Coach</Pill> : null}
                                        //old {m.IsCoach ? (m.HasCoachLicense ? <Pill tone="success">Licensed</Pill> : <Pill tone="warning">No license</Pill>) : null}
                                        //old {!m.AccountActive && <Pill tone="warning">Account pending</Pill>}
                                    //old </div>
                                //old </div>
                                //old <div className="flex flex-wrap items-center gap-3 text-sm">
                                    //old {m.AdminTypeID === 6 && (
                                        //old <label className={`inline-flex items-center gap-1.5 ${s.body}`}>
                                            //old <input type="checkbox" checked={!!m.IsOrgAdmin} disabled={busy}
                                                   //old onChange={(e) => updateRoles(m, { isOrgAdmin: e.target.checked })} />
                                            //old Org admin
                                        //old </label>
                                    //old )}
                                    //old <button type="button" className={s.btnDanger} disabled={busy} onClick={() => remove(m)}>Remove</button>
                                //old </div>
                            //old </li>
                        //old ))}
                    //old </ul>
                    //old <StaffTable staff={staff} busy={busy} onRoles={updateRoles} onRemove={remove} />
                    <StaffTable staff={staff} busy={busy} onRoles={updateRoles} onRemove={remove} adminsFull={adminsFull} />
                )}
            </Panel>
            <PendingInvites orgId={orgId} invites={invites}
                            intro="Coaches who don't have an account yet. Each was emailed a link to sign up as a coach; they join your organization once they verify their email. If the email didn't arrive, use Copy link and send it yourself. Invites expire after 30 days — Resend or Copy link starts them over."
                            onChanged={loadInvites} onError={setError} />
            </div>

            <Panel title="Add a coach or admin">
                <form onSubmit={add} className="space-y-4">
                    {/* old <Field label="Email" htmlFor="st-email" hint="Coaches without an account get an email invitation to sign up (no certification needed when you invite them). Org admins need an existing account."> */}
                    {/* someone with no account joins as a coach first, also when they're to be an org admin */}
                    <Field label="Email" htmlFor="st-email" hint="Someone without an account gets an email invitation to sign up as a coach (no certification needed when you invite them). Once they appear here, you can make them an org admin.">
                        <input id="st-email" type="email" required className={s.input} value={form.email}
                               onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                    </Field>
                    <fieldset className="space-y-2">
                        <legend className={`mb-1 text-sm font-medium ${s.body}`}>Role</legend>
                        <label className={`flex items-center gap-2 text-sm ${s.body}`}>
                            <input type="checkbox" checked={form.isCoach} onChange={(e) => setForm((f) => ({ ...f, isCoach: e.target.checked }))} />
                            {/* old Coach (sees your students once licensed) */}
                            Coach (sees your clients once licensed)
                        </label>
                        <label className={`flex items-center gap-2 text-sm ${s.body}`}>
                            {/* old <input type="checkbox" checked={form.isOrgAdmin} onChange={(e) => setForm((f) => ({ ...f, isOrgAdmin: e.target.checked }))} /> */}
                            <input type="checkbox" checked={form.isOrgAdmin} disabled={adminsFull}
                                   title={adminsFull ? `Org admin limit (${org.AdminLimit}) reached` : undefined}
                                   onChange={(e) => setForm((f) => ({ ...f, isOrgAdmin: e.target.checked }))} />
                            {/* old Org admin (manages programs, students, licenses) */}
                            Org admin (manages programs, clients, licenses)
                        </label>
                        {/* how many it can have, before anyone is added (Alicia, Sep 4: keep it visible) */}
                        {org.AdminLimit != null && (
                            <p className={`pl-6 text-xs ${s.muted}`}>
                                Org admins: {org.AdminCount} of {org.AdminLimit}{adminsFull && `, the most ${isPlatformAdmin ? "this" : "your"} organization can have`}
                            </p>
                        )}
                        {form.isCoach && (
                            <label className={`flex items-center gap-2 text-sm ${s.body}`}>
                                <input type="checkbox" checked={form.assignLicense} onChange={(e) => setForm((f) => ({ ...f, assignLicense: e.target.checked }))} />
                                {/* old Give them a coach license (if one is free when they join) */}
                                {/* an invited coach gets it on approval (assign_invited_coach_licenses), not when they join */}
                                Give them a coach license (if one is free; an invited coach gets it once Pono Pilot approves their account)
                            </label>
                        )}
                    </fieldset>
                    <button type="submit" className={`${s.btnPrimary} w-full`} disabled={busy || !form.email || (!form.isCoach && !form.isOrgAdmin)}>
                        <UserPlus size={16} /> Add
                    </button>
                </form>
            </Panel>
        </div>
    );
}


/** Coaches and org admins as a table: same data and actions as the old list
 *  (roles, coach license, account status, the org admin switch, remove). */
//old function StaffTable({ staff, busy, onRoles, onRemove }) {
// adminsFull: at the org admin limit, Org admin can only be unticked (the panel says why)
function StaffTable({ staff, busy, onRoles, onRemove, adminsFull }) {
    const s = useOrgStyles();
    if (staff.length === 0) return <p className={`px-5 py-6 text-sm ${s.muted}`}>No coaches or admins yet.</p>;
    return (
        <div className="relative overflow-x-auto">
            <table className="w-full">
                <thead>
                    <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                        <th className={s.th}>Name</th>
                        <th className={s.th}>Role</th>
                        <th className={s.th}>Coach license</th>
                        <th className={s.th}><span className="sr-only">Actions</span></th>
                    </tr>
                </thead>
                <tbody>
                    {staff.map((m) => (
                        <tr key={m.OrgStaffID} className={`border-b last:border-0 ${s.row}`}>
                            {/* this table shares the row with the add form: emails wrap, and a pending
                                account shows under the name instead of in its own column */}
                            {/* old <td className={`${s.td} [overflow-wrap:anywhere]`}> */}
                            {/* (min-w: on a phone, where the table scrolls inside its panel, a name and email don't break letter by letter) */}
                            <td className={`${s.td} min-w-[10rem] [overflow-wrap:anywhere]`}>
                                <div className={`font-semibold ${s.text}`}>{m.FirstName} {m.LastName}</div>
                                <div className={`text-xs ${s.muted}`}>{m.Email ? <EmailText email={m.Email} /> : null}</div>
                                {!m.AccountActive && <div className="mt-1"><Pill tone="warning">Account pending</Pill></div>}
                            </td>
                            <td className={s.td}>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                    {/* a coach account can also be made an org admin here */}
                                    {m.AdminTypeID === 6 ? (
                                        <>
                                            {m.IsCoach ? <Pill>Coach</Pill> : null}
                                            <label className={`inline-flex items-center gap-1.5 whitespace-nowrap text-sm ${s.body}`}>
                                                {/* old <input type="checkbox" checked={!!m.IsOrgAdmin} disabled={busy} */}
                                                <input type="checkbox" checked={!!m.IsOrgAdmin} disabled={busy || (adminsFull && !m.IsOrgAdmin)}
                                                       title={adminsFull && !m.IsOrgAdmin ? "Org admin limit reached" : undefined}
                                                       onChange={(e) => onRoles(m, { isOrgAdmin: e.target.checked })} />
                                                Org admin
                                            </label>
                                        </>
                                    ) : (
                                        <>
                                            {m.IsOrgAdmin ? <Pill tone="primary">Org admin</Pill> : null}
                                            {m.IsCoach ? <Pill>Coach</Pill> : null}
                                        </>
                                    )}
                                </div>
                            </td>
                            <td className={s.td}>
                                {m.IsCoach
                                    ? (m.HasCoachLicense ? <Pill tone="success">Licensed</Pill> : <Pill tone="warning">No license</Pill>)
                                    : <span className={s.muted}>—</span>}
                            </td>
                            <td className={`${s.td} text-right`}>
                                <button type="button" className={s.btnDanger} disabled={busy} onClick={() => onRemove(m)}>Remove</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
